<template>
    <v-dialog v-model="simulateSessionDialog" max-width="600px">
        <v-card>
            <v-card-text class="px-0 pb-0">
                <v-toolbar class="elevation-2" color="primary">
                    <v-toolbar-title class="ml-0">
                        <span class="font-weight-bold white--text">{{ $t('entities.users.simulateSession') }} {{ entity.username }}</span>
                    </v-toolbar-title>
                    <v-spacer/>
                    <v-btn @click="closeDialog" icon dark>
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-toolbar>
                <v-container>
                    <v-card class="px-4 pt-4">
                        <v-row align="center">
                            <RequiredFields/>
                            <v-col cols="12">
                                <v-col cols="12" class="pt-4 pb-0">
                                    <v-select
                                        v-model="selectedSimulatorOption"
                                        :items="simulatorOptions"
                                        :rules="[(v) => !!v || $t('validation.required')]"
                                        :menu-props="{ bottom: true, offsetY: true }"
                                        :no-data-text="$t('components.dataTableHeader.noDataAvailable')"
                                        :disabled="simulatingSession"
                                        item-text="name"
                                        class="do-not-apply-transform"
                                        prepend-inner-icon="mdi-timer-sand"
                                        return-object
                                        solo
                                        @change="simulationResult = null"
                                    >
                                        <template v-slot:label>
                                            {{ $t('entities.users.simulatorTitle') }}
                                            <span :style="{ color: 'red' }">*</span>
                                        </template>
                                    </v-select>
                                </v-col>
                                <v-form
                                    v-if="simulateSessionDialog && isSessionCostSelected"
                                    @submit.prevent="estimateSessionCost"
                                    method="POST"
                                    ref="estimateCostForm"
                                >
                                    <p>{{ $t('entities.users.enterDuration') }}</p>
                                    <v-row>
                                        <v-col cols="12" sm="6">
                                            <v-text-field
                                                v-model="wanted_playtime_hours"
                                                :label="$t('entities.users.wantedHours')"
                                                :rules="[min]"
                                                type="number"
                                            ></v-text-field>
                                        </v-col>
                                        <v-col cols="12" sm="6">
                                            <v-text-field
                                                v-model="wanted_playtime_minutes"
                                                :label="$t('entities.users.wantedMinutes')"
                                                :rules="[min]"
                                                type="number"
                                            ></v-text-field>
                                        </v-col>
                                    </v-row>

                                    <v-col cols="12" class="text-center pt-0">
                                        <v-btn
                                            @click="estimateSessionCost"
                                            :disabled="simulatingSession"
                                            :loading="simulatingSession"
                                            color="primary"
                                        >
                                            <span>{{ simulatingSession ? $t('entities.users.estimating') : ($t(`entities.users.estimateCost`)) }}</span>
                                        </v-btn>
                                    </v-col>
                                </v-form>
                                <v-form
                                    v-if="simulateSessionDialog && isSessionDurationSelected"
                                    @submit.prevent="estimateSessionDuration"
                                    method="POST"
                                    ref="estimateDurationForm"
                                >
                                    <p>{{ $t('entities.users.enterCoins') }}</p>
                                    <v-col cols="12" class="text-center pt-0">
                                        <v-text-field
                                            v-model="availableCoins"
                                            :label="$t('entities.users.coins')"
                                            :rules="[min]"
                                            type="number"
                                        ></v-text-field>
                                    </v-col>
                                    <v-col cols="12" class="text-center pt-0">
                                        <v-btn
                                            @click="estimateSessionDuration"
                                            :disabled="simulatingSession"
                                            :loading="simulatingSession"
                                            color="primary"
                                        >
                                            <span>{{ simulatingSession ? $t('entities.users.estimating') : ($t(`entities.users.estimateDuration`)) }}</span>
                                        </v-btn>
                                    </v-col>
                                </v-form>
                                <div v-if="simulateSessionDialog && simulationResult">
                                    <p>
                                        {{ $t('entities.users.estimatedPlaytime') }}
                                        {{ getHoursFromMinutes(simulationResult.duration_minutes) }}h {{getRemainingMinutes(simulationResult.duration_minutes)}}min.
                                    </p>
                                    <p>
                                        {{ $t('entities.users.estimatedCost') }}: {{ simulationResult.cost }} {{ $t('generalAttributes.coins') }}.
                                    </p>
                                    <p v-if="simulationResult.daily_boost_time_remaining_secs">
                                        {{ $t('entities.users.timeRemainingUntilDailyBoost') }}
                                        {{ getHoursFromSeconds(simulationResult.daily_boost_time_remaining_secs) }}h
                                        {{ getRemainingMinutesFromSeconds(simulationResult.daily_boost_time_remaining_secs)}}min.
                                    </p>
                                    <p v-if="!simulationResult.daily_boost_time_remaining_secs">
                                        {{ $t('entities.users.afterThatDailyBoost') }}
                                    </p>
                                </div>
                            </v-col>
                        </v-row>
                    </v-card>
                </v-container>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <v-spacer/>
                <v-btn
                    @click="closeDialog"
                    color="dark darken-1"
                    text
                >
                    {{ $t('miscellaneous.cancel') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import Vue from "vue";
import RequiredFields from "@/components/crud/RequiredFields.vue";
import Session from "@/models/main/Session";
import {mapActions} from "vuex";

export default Vue.extend({
    name: "SimulateSessionDialog",
    components: {
        RequiredFields
    },
    props: {
        entity: {
            type: Object,
            required: true
        },
        value: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            availableCoins: 0,
            wanted_playtime_hours: 0,
            wanted_playtime_minutes: 0,
            simulateSessionDialog: false,
            simulatingSession: false,
            simulationResult: null,
            selectedSimulatorOption: { name: this.$t('entities.users.estimateCost'), value: 'cost' },
            simulatorOptions: [
                { name: this.$t('entities.users.estimateCost'), value: 'cost' },
                { name: this.$t('entities.users.estimateDuration'), value: 'duration' }
            ]
        };
    },
    computed: {
        isSessionCostSelected() {
            return this.selectedSimulatorOption && this.selectedSimulatorOption.value === 'cost';
        },
        isSessionDurationSelected() {
            return this.selectedSimulatorOption && this.selectedSimulatorOption.value === 'duration';
        }
    },
    watch: {
        value(val) {
            this.simulateSessionDialog = val;
        },
        simulateSessionDialog(val) {
            this.$emit('input', val);
        }
    },
    methods: {

        ...mapActions(['showSnackbar']),

        closeDialog() {
            this.simulateSessionDialog = false;
            this.simulationResult = null;
        },
        min(value) {
            return Number(value) >= 0 || this.$t('validation.positive');
        },
        async estimateSessionDuration() {
            if (!this.$refs.estimateDurationForm.validate()) {
                return;
            }

            this.simulatingSession = true;
            const data = {
                user_id: this.entity.id,
                coins: parseInt(this.availableCoins),
            };
            try {
                const simulateSessionResponse = await Session.api.simulateSession(this.$store.getters.arena.id, data)
                    .catch(error => {
                        this.simulatingSession = false;
                        this.simulationResult = null;
                        if (error.response.status >= 300) {
                            this.showSnackbar({color:'error', message: error.response.data});
                        }
                    });
                if (simulateSessionResponse.status < 300) {
                    this.simulationResult = simulateSessionResponse.data
                }
            } catch (error) {
                console.warn(`Simulate Arena Session API Error`);
                console.log(error);
                this.simulationResult = null;
            }
            this.simulatingSession = false;
        },
        async estimateSessionCost() {
            if (!this.$store.getters.arena.id) {
                return;
            }

            if (!this.$refs.estimateCostForm.validate()) {
                return;
            }

            this.simulatingSession = true;
            const data = {
                user_id: this.entity.id,
                duration_minutes: Number(this.wanted_playtime_hours, 0) * 60 + Number(this.wanted_playtime_minutes),
            };
            try {
                const simulateSessionResponse = await Session.api.simulateSession(this.$store.getters.arena.id, data)
                    .catch(error => {
                        this.simulatingSession = false;
                        this.simulationResult = null;
                        if (error.response.status >= 300) {
                            this.showSnackbar({color:'error', message: error.response.data});
                        }
                    });
                if (simulateSessionResponse.status < 300) {
                    this.simulationResult = simulateSessionResponse.data
                }
            } catch (error) {
                console.warn(`Simulate Arena Session API Error`);
                console.log(error);
                this.simulationResult = null;
            }
            this.simulatingSession = false;
        },
        getHoursFromMinutes(minutes) {
            return Math.floor(minutes / 60);
        },
        getRemainingMinutes(minutes) {
            return minutes % 60;
        },
        getHoursFromSeconds(seconds) {
            return Math.floor(seconds / 3600);
        },
        getRemainingMinutesFromSeconds(seconds) {
            return Math.floor((seconds % 3600) / 60);
        }
    }
});
</script>

<style scoped>
/* Your styles here */
</style>